import { create } from 'zustand';
import {
  startRegistration,
  startAuthentication,
} from '@simplewebauthn/browser';
import { api } from '../lib/api';
import type { User } from '../lib/types';

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  initialized: boolean;
  init: () => Promise<void>;
  login: (epost: string) => Promise<void>;
  sendRegistrationOtp: (epost: string) => Promise<void>;
  register: (epost: string, otpCode: string, deviceNavn?: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
}

export const useAuth = create<AuthState>((set) => ({
  user: null,
  loading: false,
  error: null,
  initialized: false,

  init: async () => {
    // Registrer listener for tvungen logout (fra api.ts ved 401)
    if (typeof window !== 'undefined' && !(window as any).__authLogoutListener) {
      (window as any).__authLogoutListener = true;
      window.addEventListener('auth:logout', () => {
        set({ user: null });
      });
    }

    try {
      const { user } = await api.get<{ user: User }>('/auth/me');
      set({ user, initialized: true });
    } catch {
      set({ user: null, initialized: true });
    }
  },

  login: async (epost: string) => {
    set({ loading: true, error: null });
    try {
      // 1. Hent auth-options fra backend
      const { options, userId } = await api.post<{ options: any; userId: string }>(
        '/auth/login/options',
        { epost },
      );
      // 2. Kjør WebAuthn i nettleser
      const credential = await startAuthentication(options);
      // 3. Verifiser hos backend
      const { user } = await api.post<{ user: User }>('/auth/login/verify', {
        userId,
        credential,
      });
      set({ user, loading: false });
    } catch (err: any) {
      const msg =
        err?.name === 'NotAllowedError'
          ? 'Ingen sikkerhetsnøkkel ble brukt eller operasjonen ble avbrutt.'
          : err?.message ?? 'Innlogging feilet.';
      set({ loading: false, error: msg });
      throw err;
    }
  },

  sendRegistrationOtp: async (epost: string) => {
    set({ loading: true, error: null });
    try {
      await api.post('/auth/register/send-code', { epost });
      set({ loading: false });
    } catch (err: any) {
      const msg = err?.message ?? 'Kunne ikke sende verifiseringskode.';
      set({ loading: false, error: msg });
      throw err;
    }
  },

  register: async (epost: string, otpCode: string, deviceNavn?: string) => {
    set({ loading: true, error: null });
    try {
      const { options, userId } = await api.post<{ options: any; userId: string }>(
        '/auth/register/options',
        { epost, otpCode },
      );
      const credential = await startRegistration(options);
      await api.post('/auth/register/verify', { userId, credential, deviceNavn });
      // Etter registrering: logg inn automatisk
      await useAuth.getState().login(epost);
      set({ loading: false });
    } catch (err: any) {
      const msg =
        err?.name === 'NotAllowedError'
          ? 'Ingen sikkerhetsnøkkel ble brukt eller operasjonen ble avbrutt.'
          : err?.message ?? 'Registrering feilet.';
      set({ loading: false, error: msg });
      throw err;
    }
  },

  logout: async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Ignorer – vi tømmer state uansett
    }
    set({ user: null });
  },

  clearError: () => set({ error: null }),
}));
